/*
 * Incisione personalizzata - scadutogioielli.com
 * Motore condiviso: anteprima del testo e dei simboli sulla foto del prodotto.
 * - <engraving-picker>  : finestra per il cliente (blocco "Incisione" nella scheda prodotto)
 * - <engraving-preview> : scheda di lavoro per il negozio (template product.anteprima-incisione)
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const r1 = (v) => Math.round(v * 10) / 10;
const CLEAN_RE = /[^\p{L}\p{N} .,'&!?\-♥]/gu;

export function parseZone(str, fallback) {
  const d = Object.assign({ x: 50, y: 55, w: 45, h: 25, r: 0 }, fallback || {});
  if (!str) return d;
  const v = String(str).split(',').map(Number);
  const pick = (i, f) => (Number.isFinite(v[i]) ? v[i] : f);
  return { x: pick(0, d.x), y: pick(1, d.y), w: pick(2, d.w), h: pick(3, d.h), r: pick(4, d.r) };
}

/* Compatta/espande il disegno di un lato per l'URL della scheda di lavoro. */
export function packSide(d) {
  if (!d) return '';
  const lines = d.lines.map((l) => l.trim()).filter(Boolean);
  return [
    lines.join('||'),
    d.font.family,
    d.font.upper ? 1 : 0,
    r1(d.size),
    r1(d.x),
    r1(d.y),
    d.sym ? d.sym.id : '',
    d.sym ? r1(d.sym.size) : '',
    d.sym ? r1(d.sym.x) : '',
    d.sym ? r1(d.sym.y) : '',
    d.font.label || '',
    d.sym ? d.sym.label : '',
    d.bold ? 1 : 0,
    d.italic ? 1 : 0,
  ].join(';');
}

export function unpackSide(str) {
  if (!str) return null;
  const p = str.split(';');
  const sym = p[6]
    ? { id: p[6], size: Number(p[7]) || 20, x: Number(p[8]) || 50, y: Number(p[9]) || 50, label: p[11] || p[6] }
    : null;
  return {
    lines: p[0] ? p[0].split('||') : [],
    font: { family: p[1] || 'serif', upper: p[2] === '1', label: p[10] || p[1] || '' },
    size: Number(p[3]) || 35,
    x: Number(p[4]) || 50,
    y: Number(p[5]) || 50,
    sym,
    bold: p[12] === '1',
    italic: p[13] === '1',
  };
}

export function loadFonts(families) {
  const fam = families.filter(Boolean).map((f) => f.trim().replace(/ /g, '+'));
  if (!fam.length) return;
  const id = 'engraving-fonts-' + fam.join('-').replace(/[^a-z0-9+-]/gi, '');
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?' + fam.map((f) => 'family=' + f).join('&') + '&display=swap';
  document.head.appendChild(link);
}

/*
 * Dispone testo e simbolo di un lato dentro il suo canvas.
 * canvas: elemento position:relative con l'immagine e un .engraving__layer (absolute, inset 0)
 * zone:   {x, y, w, h, r} in % della foto (area incidibile)
 * design: {lines, font:{family, upper}, size, x, y, sym:{id, size, x, y}|null}
 * Modifica design.x/y e design.sym.x/y per tenerli dentro l'area.
 */
export function layoutSide(canvas, zone, design) {
  const layer = canvas.querySelector('.engraving__layer');
  if (!layer) return;
  const area = layer.querySelector('.engraving__area');
  const text = layer.querySelector('.engraving__text');
  const sym = layer.querySelector('.engraving__symbol');
  const cw = canvas.offsetWidth;
  const ch = canvas.offsetHeight;
  if (!cw || !ch) return;

  area.style.left = zone.x + '%';
  area.style.top = zone.y + '%';
  area.style.width = zone.w + '%';
  area.style.height = zone.h + '%';
  area.style.transform = 'translate(-50%, -50%) rotate(' + zone.r + 'deg)';

  const areaW = (cw * zone.w) / 100;
  const areaH = (ch * zone.h) / 100;
  const minX = zone.x - zone.w / 2;
  const maxX = zone.x + zone.w / 2;
  const minY = zone.y - zone.h / 2;
  const maxY = zone.y + zone.h / 2;
  const place = (el, obj, wPx, hPx) => {
    const hw = ((wPx / cw) * 100) / 2;
    const hh = ((hPx / ch) * 100) / 2;
    obj.x = hw * 2 >= zone.w ? zone.x : clamp(obj.x, minX + hw, maxX - hw);
    obj.y = hh * 2 >= zone.h ? zone.y : clamp(obj.y, minY + hh, maxY - hh);
    el.style.left = obj.x + '%';
    el.style.top = obj.y + '%';
    el.style.transform = 'translate(-50%, -50%) rotate(' + zone.r + 'deg)';
  };

  // Testo
  const lines = design.lines.map((l) => (design.font.upper ? l.trim().toUpperCase() : l.trim())).filter(Boolean);
  text.textContent = '';
  lines.forEach((l) => {
    const s = document.createElement('span');
    s.textContent = l;
    text.appendChild(s);
  });
  text.style.fontFamily = "'" + design.font.family + "', serif";
  text.style.fontWeight = design.bold ? '700' : '400';
  text.style.fontStyle = design.italic ? 'italic' : 'normal';
  text.hidden = lines.length === 0;
  if (lines.length) {
    let fs = (areaH * design.size) / 100;
    fs = Math.min(fs, areaH / (lines.length * 1.15));
    text.style.fontSize = fs + 'px';
    let maxW = 0;
    text.querySelectorAll('span').forEach((s) => {
      maxW = Math.max(maxW, s.offsetWidth);
    });
    if (maxW > areaW) {
      fs = (fs * areaW) / maxW;
      text.style.fontSize = fs + 'px';
    }
    place(text, design, text.offsetWidth, text.offsetHeight);
  }

  // Simbolo
  if (design.sym && sym) {
    sym.removeAttribute('hidden');
    const use = sym.querySelector('use');
    if (use) use.setAttribute('href', '#inc-' + design.sym.id);
    const sh = (areaH * design.sym.size) / 100;
    sym.style.width = sh + 'px';
    sym.style.height = sh + 'px';
    place(sym, design.sym, sh, sh);
  } else if (sym) {
    sym.setAttribute('hidden', '');
  }
}

/* Rileva l'area dalla foto (oggetto su fondo chiaro): ritorna una zona o null. */
export async function detectZone(img) {
  if (!img) return null;
  if (!img.complete || !img.naturalWidth) {
    await new Promise((resolve) => img.addEventListener('load', resolve, { once: true }));
  }
  try {
    const W = 160;
    const H = Math.max(1, Math.round((W * img.naturalHeight) / img.naturalWidth));
    const c = document.createElement('canvas');
    c.width = W;
    c.height = H;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, W, H);
    const d = ctx.getImageData(0, 0, W, H).data;
    const box = () => ({ x0: W, y0: H, x1: -1, y1: -1, n: 0 });
    const sat = box();
    const lum = box();
    const grow = (b, x, y) => {
      if (x < b.x0) b.x0 = x;
      if (x > b.x1) b.x1 = x;
      if (y < b.y0) b.y0 = y;
      if (y > b.y1) b.y1 = y;
      b.n++;
    };
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        const i = (y * W + x) * 4;
        if (d[i + 3] < 128) continue;
        const r = d[i];
        const g = d[i + 1];
        const b = d[i + 2];
        const mx = Math.max(r, g, b);
        const mn = Math.min(r, g, b);
        const s = mx ? (mx - mn) / mx : 0;
        const l = (r * 299 + g * 587 + b * 114) / 1000;
        if (s > 0.18 && l < 245) grow(sat, x, y);
        if (l < 200) grow(lum, x, y);
      }
    }
    const b = sat.n > W * H * 0.004 ? sat : lum;
    if (b.x1 < 0) return null;
    const bw = ((b.x1 - b.x0 + 1) / W) * 100;
    const bh = ((b.y1 - b.y0 + 1) / H) * 100;
    return {
      x: (((b.x0 + b.x1 + 1) / 2) / W) * 100,
      y: (((b.y0 + b.y1 + 1) / 2) / H) * 100,
      w: Math.max(10, bw * 0.55),
      h: Math.max(6, bh * 0.4),
      r: 0,
    };
  } catch (e) {
    return null;
  }
}

/* Carica lo sprite dei simboli una sola volta (fetch dall'asset: troppo grande per inline_asset_content). */
let spritePromise = null;
export function loadSprite(url) {
  if (document.querySelector('#engraving-sprite symbol')) return Promise.resolve(true);
  if (!url) return Promise.resolve(false);
  if (spritePromise) return spritePromise;
  spritePromise = fetch(url)
    .then((r) => r.text())
    .then((svg) => {
      const old = document.getElementById('engraving-sprite');
      if (old) old.remove();
      const wrap = document.createElement('div');
      wrap.id = 'engraving-sprite';
      wrap.setAttribute('aria-hidden', 'true');
      wrap.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden';
      wrap.innerHTML = svg.replace(/style='display:none'/, '');
      document.body.prepend(wrap);
      return true;
    })
    .catch((e) => {
      console.warn('[incisione] simboli non caricati', e);
      spritePromise = null;
      return false;
    });
  return spritePromise;
}

/* ------------------------------------------------------------------ */
/* Finestra per il cliente                                              */
/* ------------------------------------------------------------------ */
class EngravingPicker extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;
    const ds = this.dataset;
    this.saved = null;
    this.propName = ds.propName || 'Incisione';
    this.max = parseInt(ds.max, 10) || 12;
    this.maxLines = parseInt(ds.maxLines, 10) || 3;
    this.sizeMin = Number(ds.sizeMin) || 12;
    this.sizeMax = Number(ds.sizeMax) || 70;
    this.sizeDefault = Number(ds.sizeDefault) || 35;
    this.symDefault = Number(ds.symSize) || 25;
    this.sides = (ds.sides || 'fronte').split(',');
    this.guide = ds.guide === 'true' || new URLSearchParams(location.search).has('incisione_guida');
    this.sideLabels = { fronte: 'Fronte', retro: 'Retro' };

    this.dialog = this.querySelector('dialog');
    this.status = this.querySelector('[data-status]');
    this.consent = this.querySelector('[data-consent]');
    this.saveBtn = this.querySelector('[data-save]');
    this.removeBtn = this.querySelector('[data-remove]');
    this.linesBox = this.querySelector('[data-lines]');
    this.addLineBtn = this.querySelector('[data-add-line]');
    this.sizeInput = this.querySelector('[data-size]');
    this.sizeLabel = this.querySelector('[data-size-label]');
    this.propsBox = this.querySelector('[data-props]');
    this.errorBox = this.querySelector('[data-error]');
    this.canvases = {};
    this.querySelectorAll('.engraving__canvas[data-side]').forEach((c) => {
      this.canvases[c.dataset.side] = c;
    });

    const fonts = Array.from(this.querySelectorAll('input[type="radio"][data-family]'));
    this.fonts = fonts.map((r) => ({ label: r.value, family: r.dataset.family, upper: r.dataset.upper === 'true' }));
    const firstFont = this.fonts[0] || { label: '', family: 'serif', upper: false };

    this.zones = {};
    this.zoneSource = {};
    this.sides.forEach((side) => {
      const c = this.canvases[side];
      this.zones[side] = parseZone(c ? c.dataset.zone : '');
      this.zoneSource[side] = c ? c.dataset.zoneSource || 'default' : 'default';
    });

    this.design = {};
    this.sides.forEach((side) => {
      const z = this.zones[side];
      this.design[side] = {
        lines: [''],
        font: Object.assign({}, firstFont),
        size: this.sizeDefault,
        x: z.x,
        y: z.y,
        sym: null,
        sel: 'text',
        bold: false,
        italic: false,
      };
    });
    this.side = this.sides[0];

    // Apertura / chiusura
    this.querySelector('[data-open]').addEventListener('click', () => this.open());
    this.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', () => this.dialog.close()));
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.dialog.close();
    });

    // Lati
    this.querySelectorAll('[data-side-tab]').forEach((b) =>
      b.addEventListener('click', () => this.switchSide(b.dataset.sideTab))
    );

    // Caratteri
    fonts.forEach((r) =>
      r.addEventListener('change', () => {
        this.cur().font = { label: r.value, family: r.dataset.family, upper: r.dataset.upper === 'true' };
        this.render();
      })
    );

    // Stile (grassetto / corsivo)
    this.querySelectorAll('[data-style]').forEach((b) =>
      b.addEventListener('click', () => {
        const d = this.cur();
        d[b.dataset.style] = !d[b.dataset.style];
        this.render();
      })
    );

    // Righe
    if (this.addLineBtn) {
      this.addLineBtn.addEventListener('click', () => {
        const d = this.cur();
        if (d.lines.length >= this.maxLines) return;
        d.lines.push('');
        this.buildLines();
        const inputs = this.linesBox.querySelectorAll('input');
        inputs[inputs.length - 1].focus();
      });
    }

    // Dimensione
    if (this.sizeInput) {
      this.sizeInput.min = this.sizeMin;
      this.sizeInput.max = this.sizeMax;
      this.sizeInput.addEventListener('input', () => {
        const d = this.cur();
        const v = Number(this.sizeInput.value);
        if (d.sel === 'symbol' && d.sym) d.sym.size = v;
        else d.size = v;
        this.render();
      });
    }
    this.querySelectorAll('[data-sel]').forEach((b) =>
      b.addEventListener('click', () => {
        this.cur().sel = b.dataset.sel;
        this.render();
      })
    );
    const centerBtn = this.querySelector('[data-center]');
    if (centerBtn) {
      centerBtn.addEventListener('click', () => {
        const d = this.cur();
        const z = this.zones[this.side];
        if (d.sel === 'symbol' && d.sym) {
          d.sym.x = z.x;
          d.sym.y = z.y;
        } else {
          d.x = z.x;
          d.y = z.y;
        }
        this.render();
      });
    }

    // Simboli
    this.querySelectorAll('[data-symcat]').forEach((b) =>
      b.addEventListener('click', () => {
        this.querySelectorAll('[data-symcat]').forEach((o) => {
          o.classList.toggle('is-active', o === b);
          o.setAttribute('aria-selected', o === b ? 'true' : 'false');
        });
        this.querySelectorAll('[data-symgrid]').forEach((g) => {
          g.hidden = g.dataset.symgrid !== b.dataset.symcat;
        });
      })
    );
    this.querySelectorAll('[data-symbol]').forEach((b) =>
      b.addEventListener('click', () => {
        const d = this.cur();
        if (d.sym && d.sym.id === b.dataset.symbol) {
          d.sym = null;
          d.sel = 'text';
        } else {
          const z = this.zones[this.side];
          const had = d.sym;
          d.sym = {
            id: b.dataset.symbol,
            label: b.dataset.label || b.dataset.symbol,
            size: had ? had.size : this.symDefault,
            x: had ? had.x : d.x,
            y: had ? had.y : Math.min(z.y + z.h / 2, d.y + z.h * 0.3),
          };
          d.sel = 'symbol';
        }
        this.render();
      })
    );
    const noneBtn = this.querySelector('[data-sym-none]');
    if (noneBtn) {
      noneBtn.addEventListener('click', () => {
        this.cur().sym = null;
        this.cur().sel = 'text';
        this.render();
      });
    }

    // Consenso, salva, rimuovi
    this.consent.addEventListener('change', () => this.updateSaveState());
    this.saveBtn.addEventListener('click', () => this.save());
    this.removeBtn.addEventListener('click', () => this.clear());

    // Canvas: trascinamento, ridimensionamento, rilevamento area
    this.sides.forEach((side) => {
      const c = this.canvases[side];
      if (!c) return;
      this.setupDrag(c, side);
      new ResizeObserver(() => {
        if (side === this.side) this.render();
      }).observe(c);
      if (this.zoneSource[side] === 'auto') {
        detectZone(c.querySelector('img')).then((z) => {
          if (!z) return;
          this.zones[side] = z;
          const d = this.design[side];
          d.x = z.x;
          d.y = z.y;
          if (d.sym) {
            d.sym.x = z.x;
            d.sym.y = z.y;
          }
          this.render();
          this.printGuide();
        });
      }
    });
    if (document.fonts) document.fonts.addEventListener('loadingdone', () => this.render());
    if (this.guide) this.setupGuide();

    this.onCartUpdate = this.onCartUpdate.bind(this);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);

    this.buildLines();
    this.switchSide(this.side);
    this.ensureSprite();
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    this._ready = false;
  }

  cur() {
    return this.design[this.side];
  }

  hasContent(side) {
    const d = this.design[side];
    return d.lines.some((l) => l.trim()) || !!d.sym;
  }

  /* ---- apertura ---- */
  open() {
    loadFonts(this.fonts.map((f) => f.family));
    this.ensureSprite();
    this.dialog.showModal();
    requestAnimationFrame(() => {
      this.render();
      const first = this.linesBox && this.linesBox.querySelector('input');
      if (first && !first.value) first.focus();
    });
  }

  ensureSprite() {
    loadSprite(this.dataset.spriteUrl).then(() => this.render());
  }

  /* ---- lati ---- */
  switchSide(side) {
    if (!this.design[side]) return;
    this.side = side;
    this.querySelectorAll('[data-side-tab]').forEach((b) => {
      const on = b.dataset.sideTab === side;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    Object.keys(this.canvases).forEach((s) => {
      this.canvases[s].hidden = s !== side;
    });
    const d = this.cur();
    this.querySelectorAll('input[type="radio"][data-family]').forEach((r) => {
      r.checked = r.value === d.font.label;
    });
    this.buildLines();
    this.render();
    this.printGuide();
  }

  /* ---- righe di testo ---- */
  buildLines() {
    if (!this.linesBox) return;
    const d = this.cur();
    this.linesBox.textContent = '';
    d.lines.forEach((value, i) => {
      const row = document.createElement('div');
      row.className = 'engraving__line';
      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'engraving__input';
      input.maxLength = this.max;
      input.autocomplete = 'off';
      input.value = value;
      input.placeholder = 'Riga ' + (i + 1) + ', massimo ' + this.max + ' caratteri' + (i === 0 ? '*' : '');
      input.setAttribute('aria-label', 'Riga ' + (i + 1));
      input.addEventListener('input', () => {
        // Apostrofi tipografici (iPhone) convertiti in apostrofo semplice prima del filtro.
        const cleaned = input.value.replace(/[‘’ʼ`´]/g, "'").replace(CLEAN_RE, '').slice(0, this.max);
        if (cleaned !== input.value) input.value = cleaned;
        d.lines[i] = cleaned;
        d.sel = 'text';
        this.render();
      });
      row.appendChild(input);
      if (i > 0) {
        const rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'engraving__line-remove';
        rm.setAttribute('aria-label', 'Rimuovi riga ' + (i + 1));
        rm.textContent = '×';
        rm.addEventListener('click', () => {
          d.lines.splice(i, 1);
          this.buildLines();
          this.render();
        });
        row.appendChild(rm);
      }
      this.linesBox.appendChild(row);
    });
    if (this.addLineBtn) this.addLineBtn.hidden = d.lines.length >= this.maxLines;
  }

  /* ---- trascinamento ---- */
  setupDrag(canvas, side) {
    const layer = canvas.querySelector('.engraving__layer');
    if (!layer) return;
    const bind = (el, kind) => {
      el.addEventListener('pointerdown', (e) => {
        if (e.button !== 0 && e.pointerType === 'mouse') return;
        e.preventDefault();
        const d = this.design[side];
        const obj = kind === 'symbol' ? d.sym : d;
        if (!obj) return;
        d.sel = kind;
        this.render();
        const rect = canvas.getBoundingClientRect();
        const start = { px: e.clientX, py: e.clientY, x: obj.x, y: obj.y };
        el.setPointerCapture(e.pointerId);
        el.classList.add('is-dragging');
        layer.classList.add('is-dragging');
        const move = (ev) => {
          obj.x = start.x + ((ev.clientX - start.px) / rect.width) * 100;
          obj.y = start.y + ((ev.clientY - start.py) / rect.height) * 100;
          this.render();
        };
        const up = () => {
          el.removeEventListener('pointermove', move);
          el.classList.remove('is-dragging');
          layer.classList.remove('is-dragging');
          this.printGuide();
        };
        el.addEventListener('pointermove', move);
        el.addEventListener('pointerup', up, { once: true });
        el.addEventListener('pointercancel', up, { once: true });
      });
    };
    bind(layer.querySelector('.engraving__text'), 'text');
    bind(layer.querySelector('.engraving__symbol'), 'symbol');
  }

  /* ---- rendering ---- */
  render() {
    const side = this.side;
    const canvas = this.canvases[side];
    const d = this.cur();
    if (!d.sym && d.sel === 'symbol') d.sel = 'text';
    if (canvas) {
      layoutSide(canvas, this.zones[side], d);
      const layer = canvas.querySelector('.engraving__layer');
      const text = layer.querySelector('.engraving__text');
      const sym = layer.querySelector('.engraving__symbol');
      text.classList.toggle('is-selected', d.sel === 'text');
      if (sym) sym.classList.toggle('is-selected', d.sel === 'symbol');
      const hasText = d.lines.some((l) => l.trim());
      layer.classList.toggle('is-empty', !hasText && !d.sym);
      const ph = layer.querySelector('.engraving__placeholder');
      if (ph) ph.hidden = hasText || !!d.sym;
    }
    // stile
    this.querySelectorAll('[data-style]').forEach((b) => {
      const on = !!d[b.dataset.style];
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    // slider e selettore
    if (this.sizeInput) {
      this.sizeInput.value = d.sel === 'symbol' && d.sym ? d.sym.size : d.size;
    }
    if (this.sizeLabel) this.sizeLabel.textContent = d.sel === 'symbol' ? 'Dimensione del simbolo' : 'Dimensione del testo';
    this.querySelectorAll('[data-sel]').forEach((b) => {
      const on = b.dataset.sel === d.sel;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (b.dataset.sel === 'symbol') b.hidden = !d.sym;
    });
    // simboli selezionati
    this.querySelectorAll('[data-symbol]').forEach((b) => {
      b.classList.toggle('is-selected', !!d.sym && d.sym.id === b.dataset.symbol);
    });
    const noneBtn = this.querySelector('[data-sym-none]');
    if (noneBtn) noneBtn.hidden = !d.sym;
    // segni sui lati
    this.querySelectorAll('[data-side-tab]').forEach((b) => {
      b.classList.toggle('has-content', this.hasContent(b.dataset.sideTab));
    });
    this.updateSaveState();
  }

  updateSaveState() {
    const any = this.sides.some((s) => this.hasContent(s));
    this.saveBtn.disabled = !(any && this.consent.checked);
    if (this.errorBox) this.errorBox.hidden = true;
  }

  /* ---- guida per il negozio ---- */
  setupGuide() {
    this.classList.add('is-guide');
    const out = document.createElement('p');
    out.className = 'engraving__guide';
    const media = this.querySelector('.engraving__media');
    if (media) media.appendChild(out);
    this.guideOut = out;
    Object.keys(this.canvases).forEach((side) => {
      const c = this.canvases[side];
      c.addEventListener('mousemove', (e) => {
        const r = c.getBoundingClientRect();
        this.cursor = { x: r1(((e.clientX - r.left) / r.width) * 100), y: r1(((e.clientY - r.top) / r.height) * 100) };
        this.printGuide();
      });
    });
    this.printGuide();
  }

  printGuide() {
    if (!this.guideOut) return;
    const z = this.zones[this.side];
    const src = { meta: 'dai metafield del prodotto', auto: 'rilevata dalla foto', default: 'predefinita' }[this.zoneSource[this.side]] || '';
    const d = this.cur();
    let s = this.sideLabels[this.side] + ' · area incidibile (' + src + '): centro X ' + r1(z.x) + ' · centro Y ' + r1(z.y) + ' · larghezza ' + r1(z.w) + ' · altezza ' + r1(z.h) + ' · rotazione ' + r1(z.r) + '.';
    if (this.cursor) s += ' Cursore: X ' + this.cursor.x + ' · Y ' + this.cursor.y + '.';
    s += ' Testo ora a X ' + r1(d.x) + ' · Y ' + r1(d.y) + '.';
    s += ' I valori dell’area si impostano nei metafield “Incisione” del prodotto.';
    this.guideOut.textContent = s;
  }

  /* ---- salvataggio ---- */
  styleLabel(d) {
    const parts = [];
    if (d.bold) parts.push('grassetto');
    if (d.italic) parts.push('corsivo');
    return parts.join(' ');
  }

  summary(side) {
    const d = this.design[side];
    const lines = d.lines.map((l) => (d.font.upper ? l.trim().toUpperCase() : l.trim())).filter(Boolean);
    let s = lines.join(' | ');
    if (d.sym) s += (s ? ' + ' : '') + 'simbolo: ' + d.sym.label;
    return s;
  }

  previewUrl() {
    const base = this.dataset.productUrl || location.pathname;
    const params = new URLSearchParams();
    params.set('view', 'anteprima-incisione');
    this.sides.forEach((side) => {
      if (!this.hasContent(side)) return;
      params.set(side === 'retro' ? 'r' : 'f', packSide(this.design[side]));
      // Area incidibile usata nell'anteprima del cliente (anche quella rilevata dalla foto).
      const z = this.zones[side];
      if (z) params.set(side === 'retro' ? 'zr' : 'zf', [z.x, z.y, z.w, z.h, z.r || 0].map(r1).join(','));
    });
    // Variante scelta: la scheda di lavoro mostra la stessa foto vista dal cliente.
    const form = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
    const variantInput = form ? form.querySelector('[name="id"]') : null;
    if (variantInput && variantInput.value) params.set('variant', variantInput.value);
    return location.origin + base.split('?')[0] + '?' + params.toString();
  }

  addProp(name, value) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[' + name + ']';
    input.value = value;
    input.setAttribute('form', this.dataset.formId);
    this.propsBox.appendChild(input);
  }

  save() {
    const sides = this.sides.filter((s) => this.hasContent(s));
    if (!sides.length || !this.consent.checked) return;
    const token = Math.random().toString(36).slice(2, 8).toUpperCase() + Date.now().toString(36).slice(-4).toUpperCase();
    this.propsBox.textContent = '';
    const statusParts = [];
    sides.forEach((side) => {
      const d = this.design[side];
      const label = this.sideLabels[side];
      const hasText = d.lines.some((l) => l.trim());
      this.addProp(this.propName + ' ' + label.toLowerCase(), this.summary(side));
      const styleLabel = this.styleLabel(d);
      if (hasText) this.addProp(this.propName + ' ' + label.toLowerCase() + ' - carattere', d.font.label + (styleLabel ? ', ' + styleLabel : ''));
      let det = hasText ? 'testo ' + r1(d.size) + '% a X ' + r1(d.x) + ' Y ' + r1(d.y) : 'nessun testo';
      if (d.sym) det += '; simbolo ' + d.sym.id + ' ' + r1(d.sym.size) + '% a X ' + r1(d.sym.x) + ' Y ' + r1(d.sym.y);
      const z = this.zones[side];
      det += '; area ' + [z.x, z.y, z.w, z.h, z.r].map(r1).join(',');
      this.addProp('_' + this.propName + ' ' + label.toLowerCase() + ' - dettagli', det);
      statusParts.push(label + ': ' + this.summary(side) + (hasText ? ' (' + d.font.label + (styleLabel ? ', ' + styleLabel : '') + ')' : ''));
    });
    this.addProp('_' + this.propName + ' - scheda di lavoro', this.previewUrl());
    this.addProp('_' + this.propName + ' - id', token);
    this.saved = { token, sides };
    this.status.textContent = statusParts.join(' · ');
    this.classList.add('has-engraving');
    this.removeBtn.hidden = false;
    this.dialog.close();
  }

  clear() {
    this.propsBox.textContent = '';
    this.saved = null;
    this.sides.forEach((side) => {
      const d = this.design[side];
      const z = this.zones[side];
      d.lines = [''];
      d.size = this.sizeDefault;
      d.x = z.x;
      d.y = z.y;
      d.sym = null;
      d.sel = 'text';
      d.bold = false;
      d.italic = false;
    });
    this.consent.checked = false;
    this.status.textContent = this.dataset.none || '';
    this.classList.remove('has-engraving');
    this.removeBtn.hidden = true;
    this.switchSide(this.sides[0]);
    this.dialog.close();
  }

  /* ---- riga servizio nel carrello ---- */
  onCartUpdate(event) {
    if (!this.saved || !this.dataset.serviceVariant) return;
    if (!event.promise) return;
    event.promise
      .then((result) => {
        const detail = result && result.detail;
        if (!detail || detail.didError) return;
        if (detail.source !== 'product-form-component') return;
        if (String(detail.productId) !== this.dataset.productId) return;
        return this.syncService(detail.items || []);
      })
      .catch((error) => console.warn('[incisione]', error));
  }

  async syncService(items) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const serviceId = Number(this.dataset.serviceVariant);
    const idKey = '_' + this.propName + ' - id';
    const parent = items.find((i) => i.properties && i.properties[idKey] === this.saved.token);
    if (!parent) return;
    // Prezzo unico per pezzo: fronte ed eventuale retro sono compresi in un solo servizio.
    const wanted = parent.quantity;
    const child = items.find(
      (i) => i.variant_id === serviceId && i.parent_relationship && i.parent_relationship.parent_key === parent.key
    );
    if (child && child.quantity === wanted) return;

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: child ? 'update' : 'add',
        context: 'product',
        lines: [{ merchandiseId: String(serviceId), quantity: wanted }],
        promise: deferred.promise,
      })
    );
    try {
      const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
      const lati = this.saved.sides.map((s) => this.sideLabels[s]).join(' e ');
      const request = child
        ? fetch(root + 'cart/change.js', { method: 'POST', headers, body: JSON.stringify({ id: child.key, quantity: wanted }) })
        : fetch(root + 'cart/add.js', {
            method: 'POST',
            headers,
            body: JSON.stringify({
              items: [
                {
                  id: serviceId,
                  quantity: wanted,
                  parent_line_key: parent.key,
                  properties: { Articolo: parent.product_title, Lati: lati },
                },
              ],
            }),
          });
      const response = await request;
      const body = await response.json();
      if (body && body.status) throw new Error(body.description || body.message || 'Errore incisione');
      const cart = await (await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })).json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: wanted, source: 'engraving-picker', didError: false },
      });
    } catch (error) {
      deferred.reject(error);
      console.warn('[incisione]', error);
      // Il servizio a pagamento non e' entrato nel carrello: avvisa il cliente invece di fallire in silenzio.
      const msg =
        "Non siamo riusciti ad aggiungere il servizio di incisione al carrello. Ricarica la pagina e riprova, oppure contattaci: senza il servizio l'incisione non puo' essere eseguita.";
      if (this.errorBox) {
        this.errorBox.textContent = msg;
        this.errorBox.hidden = false;
      }
      window.alert(msg);
    }
  }
}

/* ------------------------------------------------------------------ */
/* Scheda di lavoro per il negozio                                      */
/* ------------------------------------------------------------------ */
class EngravingPreview extends HTMLElement {
  connectedCallback() {
    const params = new URLSearchParams(location.search);
    const designs = { fronte: unpackSide(params.get('f')), retro: unpackSide(params.get('r')) };
    const zoneParams = { fronte: params.get('zf'), retro: params.get('zr') };
    const zones = {};
    const families = [];
    let any = false;
    ['fronte', 'retro'].forEach((side) => {
      const block = this.querySelector('[data-preview-side="' + side + '"]');
      if (!block) return;
      const d = designs[side];
      if (!d) {
        block.hidden = true;
        return;
      }
      block.hidden = false;
      any = true;
      families.push(d.font.family);
      const canvas = block.querySelector('.engraving__canvas');
      const zone = parseZone(zoneParams[side] || canvas.dataset.zone);
      zones[side] = zone;
      const draw = () => layoutSide(canvas, zone, d);
      draw();
      new ResizeObserver(draw).observe(canvas);
      const img = canvas.querySelector('img');
      if (img && !img.complete) img.addEventListener('load', draw, { once: true });
      // dettagli
      const list = block.querySelector('[data-details]');
      const add = (k, v) => {
        const dt = document.createElement('dt');
        dt.textContent = k;
        const dd = document.createElement('dd');
        dd.textContent = v;
        list.appendChild(dt);
        list.appendChild(dd);
      };
      const lines = d.lines.map((l) => (d.font.upper ? l.toUpperCase() : l)).filter(Boolean);
      lines.forEach((l, i) => add('Riga ' + (i + 1), l));
      if (lines.length) {
        add('Carattere', d.font.label + ' (' + d.font.family + (d.font.upper ? ', maiuscolo' : '') + ')');
        add('Stile', [d.bold ? 'grassetto' : '', d.italic ? 'corsivo' : ''].filter(Boolean).join(' ') || 'normale');
        add('Dimensione testo', r1(d.size) + '% dell’altezza dell’area');
        add('Centro testo', 'X ' + r1(d.x) + '% · Y ' + r1(d.y) + '% della foto');
      }
      if (d.sym) {
        add('Simbolo', d.sym.label + ' (' + d.sym.id + '.svg)');
        add('Dimensione simbolo', r1(d.sym.size) + '% dell’altezza dell’area');
        add('Centro simbolo', 'X ' + r1(d.sym.x) + '% · Y ' + r1(d.sym.y) + '% della foto');
        const dl = block.querySelector('[data-download-symbol]');
        if (dl) {
          dl.hidden = false;
          dl.addEventListener('click', () => {
            const symbol = document.getElementById('inc-' + d.sym.id);
            if (!symbol) return;
            const svg =
              '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24mm" height="24mm" fill="none" stroke="#000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">' +
              symbol.innerHTML +
              '</svg>';
            const a = document.createElement('a');
            a.href = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
            a.download = 'simbolo-' + d.sym.id + '.svg';
            a.click();
          });
        }
      }
      add('Area incidibile', 'centro X ' + r1(zone.x) + ' · Y ' + r1(zone.y) + ' · larghezza ' + r1(zone.w) + ' · altezza ' + r1(zone.h) + ' (% della foto)');
    });
    loadFonts(families);
    const redraw = () => {
      this.querySelectorAll('.engraving__canvas').forEach((c) => {
        const side = c.closest('[data-preview-side]').dataset.previewSide;
        if (designs[side]) layoutSide(c, zones[side] || parseZone(c.dataset.zone), designs[side]);
      });
    };
    loadSprite(this.dataset.spriteUrl).then(redraw);
    if (document.fonts) document.fonts.addEventListener('loadingdone', redraw);
    const empty = this.querySelector('[data-empty]');
    if (empty) empty.hidden = any;
    const printBtn = this.querySelector('[data-print]');
    if (printBtn) printBtn.addEventListener('click', () => window.print());
  }
}

if (!customElements.get('engraving-picker')) customElements.define('engraving-picker', EngravingPicker);
if (!customElements.get('engraving-preview')) customElements.define('engraving-preview', EngravingPreview);
